import type {
  SetupStatusResponse,
  InitializeSetupResponse,
  SetupProgressResponse,
  CompleteStepResponse,
  FinalizeSetupResponse,
  StepValidationData,
  VerificationStepType,
  WhatsAppReverseStartResponse,
  WhatsAppReverseStatusResponse,
  WhatsAppConfigResponse,
} from '@/pages/MfaCompleteSetup/types';

import api from '../api';

import { experienceApiRoutes } from './const';

// Which WhatsApp verification mode is active (reverse-OTP vs legacy forward OTP).
export const getWhatsAppConfig = async (): Promise<WhatsAppConfigResponse> =>
  api
    .get(`${experienceApiRoutes.prefix}/verification-setup/whatsapp/config`)
    .json<WhatsAppConfigResponse>();

// Legacy forward OTP: ask the server to send a code via the AdMedia WhatsApp connector.
export const sendWhatsAppCode = async (phone: string): Promise<{ success: boolean }> =>
  api
    .post(`${experienceApiRoutes.prefix}/verification-setup/whatsapp/send-code`, {
      json: { phone },
    })
    .json<{ success: boolean }>();

// Reverse-OTP WhatsApp: start a verification, returning the wa.me deep link the user taps to send
// a free inbound message (instead of us sending a billable OTP template).
export const startWhatsAppReverse = async (
  phone: string
): Promise<WhatsAppReverseStartResponse> =>
  api
    .post(`${experienceApiRoutes.prefix}/verification-setup/whatsapp-reverse/start`, {
      json: { phone },
    })
    .json<WhatsAppReverseStartResponse>();

// Reverse-OTP WhatsApp: poll whether the inbound message has arrived and verified the session.
// The verification is keyed to the session server-side, so no verificationId is sent by the client.
export const getWhatsAppReverseStatus = async (): Promise<WhatsAppReverseStatusResponse> =>
  api
    .get(`${experienceApiRoutes.prefix}/verification-setup/whatsapp-reverse/status`)
    .json<WhatsAppReverseStatusResponse>();

// Restore session from encrypted userId (for OAuth callback flows)
export const restoreSessionFromUserId = async (userId: string) => {
  // Try to establish a session context that can decrypt the userId
  // This should be called before any other API calls that require the userId
  return api.put(`${experienceApiRoutes.prefix}/session/restore`, {
    json: {
      encryptedUserId: userId,
      interactionEvent: 'SignIn',
    },
  });
};

// Get verification setup status
export const getVerificationSetupStatus = async (userId?: string): Promise<SetupStatusResponse> => {
  let url = `${experienceApiRoutes.prefix}/verification-setup/status`;
  if (userId) {
    url += `?userId=${userId}`; // Don't encode here, let the API handle it
  }
  return api.get(url).json<SetupStatusResponse>();
};

// Initialize verification setup process
export const initializeVerificationSetup = async (data?: {
  userAgent?: string;
  clientInfo?: {
    platform: string;
    version: string;
  };
}): Promise<InitializeSetupResponse> =>
  api
    .post(`${experienceApiRoutes.prefix}/verification-setup/initialize`, {
      json: data || {},
    })
    .json<InitializeSetupResponse>();

// Get current verification setup progress
export const getVerificationSetupProgress = async (): Promise<SetupProgressResponse> =>
  api
    .get(`${experienceApiRoutes.prefix}/verification-setup/progress`)
    .json<SetupProgressResponse>();

// Complete a specific verification step
export const completeVerificationStep = async (
  stepType: VerificationStepType,
  validationData: StepValidationData
): Promise<CompleteStepResponse> =>
  api
    .post(`${experienceApiRoutes.prefix}/verification-setup/step/${stepType}/complete`, {
      json: validationData,
    })
    .json<CompleteStepResponse>();

// Finalize the entire setup process
export const finalizeVerificationSetup = async (data: {
  confirmCompletion: boolean;
}): Promise<FinalizeSetupResponse> =>
  api
    .post(`${experienceApiRoutes.prefix}/verification-setup/finalize`, {
      json: data,
    })
    .json<FinalizeSetupResponse>();

// Skip setup (admin override)
export const skipVerificationSetup = async (data: {
  reason: string;
  adminToken: string;
}): Promise<{
  success: boolean;
  skippedAt: string;
  reason: string;
}> =>
  api
    .post(`${experienceApiRoutes.prefix}/verification-setup/skip`, {
      json: data,
    })
    .json<{
      success: boolean;
      skippedAt: string;
      reason: string;
    }>();
